import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';
import { TIERS, PLATFORM } from './content';

const TIER_OPTIONS = TIERS.map((t) => ({ id: t.id, name: t.name }));
const ENGINEER_OPTIONS = ['1–2 (sole trader / small)', '3–10', '11–25', '26–50', '50+'];

const EMPTY = {
  agency_name: '',
  contact_name: '',
  email: '',
  phone: '',
  engineers: ENGINEER_OPTIONS[1],
  tier: 'professional',
  notes: ''
};

export default function SignUp() {
  const [params] = useSearchParams();
  const initialTier = params.get('tier') || 'professional';
  const [form, setForm] = useState({ ...EMPTY, tier: initialTier });
  const [touched, setTouched] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email), [form.email]);
  const agencyValid = form.agency_name.trim().length >= 2;
  const contactValid = form.contact_name.trim().length >= 2;
  const canSubmit = agencyValid && contactValid && emailValid && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setTouched({ agency_name: true, contact_name: true, email: true });
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/signups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(
        err.message === 'Failed to fetch'
          ? 'Network error — please check your connection and try again.'
          : err.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const tier = TIERS.find((t) => t.id === form.tier);

  if (done) {
    return (
      <section className="mk-section">
        <div className="mk-container" style={{ maxWidth: 640 }}>
          <div className="mk-form mk-success">
            <span className="mk-success-icon"><SafeIcon icon={Icons.CheckCircle} /></span>
            <h2>Thanks — request received</h2>
            <p>
              We&apos;ll be in touch within one working day to set up{' '}
              <strong>{form.agency_name}</strong> on the {tier?.name || 'selected'} plan.
              Watch your inbox ({form.email}) — we&apos;ll send your admin login and
              onboarding link there.
            </p>
            <div className="mk-hero-ctas" style={{ justifyContent: 'center' }}>
              <Link to="/" className="mk-btn mk-btn-outline">Back to home</Link>
              <Link to="/features" className="mk-btn mk-btn-primary">Explore features</Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mk-section" style={{ paddingTop: 54 }}>
      <div className="mk-container mk-signup">
        {/* ---------- left: pitch + steps ---------- */}
        <div>
          <span className="mk-eyebrow">Get started</span>
          <h2 style={{ fontSize: 'clamp(26px, 3.4vw, 36px)', lineHeight: 1.15 }}>
            Set up your agency portal
          </h2>
          <p style={{ marginTop: 12, color: 'var(--mk-muted)', fontSize: 15.5, lineHeight: 1.7 }}>
            Tell us about your agency and we&apos;ll provision your workspace: branding,
            first clients, engineer accounts and portal links. No card required to start.
          </p>

          <ol className="mk-steps">
            <li>
              <span className="mk-step-n">1</span>
              <div>
                <strong>Send us your details</strong>
                <span>Takes 60 seconds — the form on the right.</span>
              </div>
            </li>
            <li>
              <span className="mk-step-n">2</span>
              <div>
                <strong>We provision your workspace</strong>
                <span>Your branding, invoice defaults and key-date types configured.</span>
              </div>
            </li>
            <li>
              <span className="mk-step-n">3</span>
              <div>
                <strong>Invite your clients &amp; engineers</strong>
                <span>Magic-link emails for clients, login accounts for engineers.</span>
              </div>
            </li>
            <li>
              <span className="mk-step-n">4</span>
              <div>
                <strong>Go live</strong>
                <span>First invoice, first job sheet, first certificate — same day.</span>
              </div>
            </li>
          </ol>

          <div className="mk-offer">
            <strong>Launch offer:</strong> the first 10 agencies get 3 months at 50% off
            and a free data import. Monthly billing, cancel anytime, full data export if
            you leave.
          </div>
        </div>

        {/* ---------- right: form ---------- */}
        <form className="mk-form" onSubmit={submit} noValidate>
          <h2>Request your workspace</h2>
          <p>
            {PLATFORM.name} — we&apos;ll reply within one working day. Or email
            hello@during.uk directly.
          </p>

          <div className="mk-form-grid">
            <div className="mk-field mk-field-wide">
              <label htmlFor="su-agency">Agency name</label>
              <input
                id="su-agency"
                type="text"
                value={form.agency_name}
                onChange={set('agency_name')}
                onBlur={() => setTouched((t) => ({ ...t, agency_name: true }))}
                className={touched.agency_name && !agencyValid ? 'mk-invalid' : ''}
                placeholder="Premiere Plumbing &amp; Heating"
                autoComplete="organization"
                required
              />
            </div>

            <div className="mk-field">
              <label htmlFor="su-contact">Your name</label>
              <input
                id="su-contact"
                type="text"
                value={form.contact_name}
                onChange={set('contact_name')}
                onBlur={() => setTouched((t) => ({ ...t, contact_name: true }))}
                className={touched.contact_name && !contactValid ? 'mk-invalid' : ''}
                placeholder="Jo Smith"
                autoComplete="name"
                required
              />
            </div>

            <div className="mk-field">
              <label htmlFor="su-email">Work email</label>
              <input
                id="su-email"
                type="email"
                value={form.email}
                onChange={set('email')}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                className={touched.email && !emailValid ? 'mk-invalid' : ''}
                placeholder="jo@agency.co.uk"
                autoComplete="email"
                required
              />
            </div>

            <div className="mk-field">
              <label htmlFor="su-phone">
                Phone <em>(optional)</em>
              </label>
              <input
                id="su-phone"
                type="tel"
                value={form.phone}
                onChange={set('phone')}
                placeholder="07…"
                autoComplete="tel"
              />
            </div>

            <div className="mk-field">
              <label htmlFor="su-engineers">
                Engineers <em>(optional)</em>
              </label>
              <select id="su-engineers" value={form.engineers} onChange={set('engineers')}>
                {ENGINEER_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="mk-field mk-field-wide">
              <label htmlFor="su-tier">Plan</label>
              <select id="su-tier" value={form.tier} onChange={set('tier')}>
                {TIER_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            <div className="mk-field mk-field-wide">
              <label htmlFor="su-notes">
                Anything else? <em>(optional)</em>
              </label>
              <textarea
                id="su-notes"
                value={form.notes}
                onChange={set('notes')}
                placeholder="Number of clients, current software, Gas Safe reg, must-haves…"
                maxLength={2000}
              />
            </div>
          </div>

          {error && <div className="mk-form-error" role="alert">{error}</div>}

          <div className="mk-hero-ctas" style={{ marginTop: 20 }}>
            <button type="submit" className="mk-btn mk-btn-primary" disabled={!canSubmit}>
              {submitting ? 'Sending…' : 'Request my workspace'}
              {!submitting && <SafeIcon icon={Icons.ArrowRight} />}
            </button>
          </div>

          <p className="mk-form-privacy">
            We only use these details to set up and contact you about your workspace.
            No marketing lists, no sharing — your data is yours (full export on request).
          </p>
        </form>
      </div>
    </section>
  );
}
